import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { normalizeSpaces } from '../../../utils/dbText';
import { Link } from 'react-router-dom';

import { emailRegex, passWordRegex } from '../../../constants.js/misc';
import OnboardingLayout from '../components/OnboardingLayout/OnboardingLayout';
import StepHeader from '../components/StepHeader/StepHeader';
import { TextField, PasswordField } from '../components/Field/Field';
import { Button } from '../components/Button/Button';
import Callout from '../components/Callout/Callout';
import { Fields, AlertSlot, Suggestion } from './steps.styles';

const schema = yup.object({
    name: yup
        .string()
        .transform((value) => normalizeSpaces(value))
        .required('SignUpV2.errors.nameRequired')
        .min(2, 'SignUpV2.errors.nameShort')
        .max(80, 'SignUpV2.errors.nameLong'),
    email: yup
        .string()
        .trim()
        .required('SignUpV2.errors.emailRequired')
        .matches(emailRegex, 'SignUpV2.errors.emailFormat'),
    password: yup
        .string()
        .required('SignUpV2.errors.passwordRequired')
        .matches(passWordRegex, 'SignUpV2.errors.passwordShort'),
});

const DOMAIN_TYPOS = {
    'gmial.com': 'gmail.com',
    'gmal.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gamil.com': 'gmail.com',
    'gmail.co': 'gmail.com',
    'gmail.con': 'gmail.com',
    'hotmal.com': 'hotmail.com',
    'hotmial.com': 'hotmail.com',
    'hotmail.co': 'hotmail.com',
    'hotmail.con': 'hotmail.com',
    'outlok.com': 'outlook.com',
    'yaho.com': 'yahoo.com',
    'yahoo.co': 'yahoo.com',
};

/** 'laura@gmial.com' → 'laura@gmail.com' (client-side hint only). */
export const suggestEmail = (email) => {
    const [user, domain] = String(email || '')
        .trim()
        .toLowerCase()
        .split('@');
    if (!user || !domain || !DOMAIN_TYPOS[domain]) return null;
    return `${user}@${DOMAIN_TYPOS[domain]}`;
};

const ORDER = ['name', 'email', 'password'];

/** Step 1 · cuenta: name, email and password. No network call. */
const AccountStep = ({ ctx }) => {
    const {
        t,
        isDemo,
        draft,
        updateDraft,
        passwordRef,
        emailInUse,
        setEmailInUse,
        passwordMissing,
        setPasswordMissing,
        goTo,
        trackStep,
        analytics,
    } = ctx;

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        setFocus,
        setValue,
        clearErrors,
        watch,
    } = useForm({
        resolver: yupResolver(schema),
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: {
            name: draft.name || '',
            email: draft.email || '',
            password: passwordRef.current || '',
        },
    });

    const email = watch('email');
    const password = watch('password') || '';
    const suggestion = suggestEmail(email);
    const passwordOk = passWordRegex.test(password);
    // Only when arriving with a restored draft (reload): the password is never persisted.
    const [restoredWithoutPassword] = useState(
        () => passwordMissing || (!!draft.email && !passwordRef.current)
    );

    useEffect(() => {
        if (emailInUse) {
            setError('email', { type: 'emailInUse', message: 'SignUpV2.errors.emailInUse' });
            setFocus('email');
        } else if (restoredWithoutPassword) {
            setFocus('password');
        }
        // Only when arriving at the step.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const subscription = watch((values, { name }) => {
            if (name === 'name' || name === 'email') {
                updateDraft({ name: values.name, email: values.email });
            }
            if (name === 'email' && emailInUse) setEmailInUse(false);
        });
        return () => subscription.unsubscribe();
    }, [watch, updateDraft, emailInUse, setEmailInUse]);

    const focusNextOnEnter = (field) => (event) => {
        if (event.key !== 'Enter') return;
        const next = ORDER[ORDER.indexOf(field) + 1];
        if (next) {
            event.preventDefault();
            setFocus(next);
        }
    };

    const onValid = (values) => {
        const clean = { name: values.name.trim(), email: values.email.trim().toLowerCase() };
        updateDraft(clean);
        passwordRef.current = values.password;
        setPasswordMissing(false);
        trackStep('signup_step_completed');
        goTo('nicho');
    };

    const onInvalid = (fieldErrors) => {
        Object.keys(fieldErrors).forEach((field) =>
            analytics.track('signup_validation_error', {
                step_name: 'cuenta',
                field,
                error_code: fieldErrors[field].type,
            })
        );
    };

    const errorText = (field) => {
        const error = errors[field];
        if (!error) return null;
        if (error.type === 'emailInUse') {
            return (
                <>
                    {t('SignUpV2.errors.emailInUse')}{' '}
                    <Link to="/">{t('SignUpV2.header.login')}</Link>
                </>
            );
        }
        return t(error.message);
    };

    const nameField = register('name');
    const emailField = register('email');

    return (
        <OnboardingLayout
            preview={ctx.preview}
            step={ctx.stepNumberOf('cuenta')}
            total={ctx.totalSteps}
            stepKey="cuenta"
            title={t('SignUpV2.cuenta.title')}
            headingId="cuenta-title"
            isDemo={isDemo}
            showLoginLink
            onSubmit={handleSubmit(onValid, onInvalid)}
            footer={
                <Button type="submit" iconRight="ri-arrow-right-line">
                    {t('SignUpV2.cuenta.cta')}
                </Button>
            }
        >
            <StepHeader
                id="cuenta-title"
                title={t('SignUpV2.cuenta.title')}
                subtitle={t('SignUpV2.cuenta.subtitle')}
            />
            <AlertSlot>
                {restoredWithoutPassword && !emailInUse && (
                    <Callout variant="info">
                        <p>{t('SignUpV2.cuenta.passwordAgain')}</p>
                    </Callout>
                )}
            </AlertSlot>
            <Fields>
                <TextField
                    id="signup-name"
                    label={t('SignUpV2.cuenta.nameLabel')}
                    placeholder={t('SignUpV2.cuenta.namePlaceholder')}
                    type="text"
                    autoComplete="name"
                    autoCapitalize="words"
                    enterkeyhint="next"
                    maxLength={80}
                    error={errorText('name')}
                    {...nameField}
                    onKeyDown={focusNextOnEnter('name')}
                />
                <TextField
                    id="signup-email"
                    label={t('SignUpV2.cuenta.emailLabel')}
                    placeholder={t('SignUpV2.cuenta.emailPlaceholder')}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    enterkeyhint="next"
                    error={errorText('email')}
                    {...emailField}
                    onKeyDown={focusNextOnEnter('email')}
                />
                {suggestion && (
                    <Suggestion
                        type="button"
                        onClick={() => {
                            setValue('email', suggestion, {
                                shouldValidate: true,
                                shouldDirty: true,
                            });
                            clearErrors('email');
                        }}
                    >
                        {t('SignUpV2.cuenta.emailSuggestion')}
                        <strong>{suggestion}</strong>
                        {t('SignUpV2.cuenta.emailSuggestionEnd')}
                    </Suggestion>
                )}
                <PasswordField
                    id="signup-password"
                    label={t('SignUpV2.cuenta.passwordLabel')}
                    autoComplete="new-password"
                    enterkeyhint="go"
                    hint={t('SignUpV2.cuenta.passwordHint')}
                    hintOk={passwordOk}
                    error={errorText('password')}
                    {...register('password')}
                />
            </Fields>
        </OnboardingLayout>
    );
};

export default AccountStep;
